'use client'

import { Title, Text, Stack, Grid, GridCol, Center, Divider, Button } from '@mantine/core'
import { IconCompass, IconRocket, IconTrendingUp, IconUserEdit, IconUsers, IconStar, IconArrowUp, IconBrain, IconArrowRight } from '@tabler/icons-react'
import Link from 'next/link'
import styles from './ProcessSection.module.css'

const processContent = {
  mentee: {
    iconBackgroundColor: 'var(--mantine-color-marigold-1)',
    steps: [
      {
        icon: IconRocket,
        title: 'Unblock Work Challenges',
        description: 'Stuck on a critical project? Get unstuck with someone who\'s solved this exact problem before and brings fresh eyes to your situation.'
      },
      {
        icon: IconCompass,
        title: 'Navigate Career Moves',
        description: 'Planning your next role, pivoting into tech or government, or pushing for that promotion? Learn what actually works from leaders who\'ve made these moves.'
      },
      {
        icon: IconTrendingUp,
        title: 'Validate Your Startup Fast',
        description: 'Stop asking your mates. Get direct, honest feedback from founders who\'ve been through it - no sugar-coating.'
      }
    ]
  },
  mentor: {
    iconBackgroundColor: 'var(--mantine-color-pistachio-1)',
    steps: [
      {
        icon: IconArrowUp,
        title: 'Send the elevator back down',
        description: 'You got here because someone helped. Now it\'s your turn, but you\'re busy shipping products and leading teams, not running a side coaching business. Structured mentoring lets you have meaningful impact in focused sessions.'
      },
      {
        icon: IconBrain,
        title: 'Sharpen your edge',
        description: 'Every session forces you to articulate your decisions and break down problems into teachable frameworks, strengthening your own leadership.'
      },
      {
        icon: IconStar,
        title: 'Focus on impact, not admin',
        description: 'Keep the meaningful conversations while we handle scheduling, payments, notes, and follow-ups. Better mentoring with less busy work.'
      }
    ]
  }
}

export default function ProcessSection() {
  return (
    <Stack gap={80} style={{ textAlign: 'center' }}>
      {/* Mentor Section */}
      <Stack gap="xl">
        <Title order={2} size="h1" className="gradient-emerald">
          Interested in mentoring?
        </Title>

        <Grid gutter="xl" pt="xl">
          {processContent.mentor.steps.map((step, index) => (
            <GridCol key={index} span={{ base: 12, sm: 4 }}>
              <Stack gap="md" align="center">
                <Center
                  style={{
                    width: 80,
                    height: 80,
                    borderRadius: '50%',
                    backgroundColor: processContent.mentor.iconBackgroundColor,
                  }}
                >
                  <step.icon size={32} className="gradient-icon" />
                </Center>

                <Title order={3} c="navy.8">
                  {step.title}
                </Title>

                <Text size="lg" c="gray.7" style={{ maxWidth: '350px', lineHeight: 1.7 }}>
                  {step.description}
                </Text>
              </Stack>
            </GridCol>
          ))}
        </Grid>

        <Center mt="xl">
          <Link href="/become-a-mentor" style={{ textDecoration: 'none' }}>
            <Button size="lg" variant="gradient" gradient={{ from: 'emerald', to: 'teal', deg: 90 }}>
              Become a Mentor
            </Button>
          </Link>
        </Center>
      </Stack>

      {/* Section Divider */}
      <Divider
        size="sm"
        w="60%"
        mx="auto"
        color="emerald.4"
        styles={{
          root: { maxWidth: '600px' }
        }}
      />

      {/* Mentee Section */}
      <Stack gap="xl">
        <Title order={2} size="h1" className="gradient-emerald">
          Looking for a mentor?
        </Title>

        <Grid gutter="xl" pt="xl">
          {processContent.mentee.steps.map((step, index) => (
            <GridCol key={index} span={{ base: 12, sm: 4 }}>
              <Stack gap="md" align="center">
                <Center
                  style={{
                    width: 80,
                    height: 80,
                    borderRadius: '50%',
                    backgroundColor: processContent.mentee.iconBackgroundColor,
                  }}
                >
                  <step.icon size={32} className="gradient-icon" />
                </Center>

                <Title order={3} c="navy.8">
                  {step.title}
                </Title>

                <Text size="lg" c="gray.7" style={{ maxWidth: '350px', lineHeight: 1.7 }}>
                  {step.description}
                </Text>
              </Stack>
            </GridCol>
          ))}
        </Grid>

        <Center mt="xl">
          <Link href="/mentors" style={{ textDecoration: 'none' }}>
            <Button size="lg" variant="gradient" gradient={{ from: 'marigold', to: 'orange', deg: 90 }}>
              Browse Mentors
            </Button>
          </Link>
        </Center>
      </Stack>

      {/* Section Divider */}
      <Divider
        size="sm"
        w="60%"
        mx="auto"
        color="emerald.4"
        styles={{
          root: { maxWidth: '600px' }
        }}
      />

      {/* Team Section */}
      <Stack gap="lg" align="center" style={{ maxWidth: '700px', margin: '0 auto' }}>
        <Title order={3} size="h3" c="navy.8">
          Building a team?
        </Title>
        <Text size="lg" c="gray.7" style={{ lineHeight: 1.7 }}>
          Give your team access to external mentors who bring fresh perspectives and specialized expertise. 
          Fill knowledge gaps and accelerate team growth without the hiring marathon.
        </Text>
        <Link href="/mentoring-for-teams" style={{ textDecoration: 'none' }}>
          <Button 
            size="md" 
            variant="outline" 
            color="navy"
            rightSection={<IconArrowRight size={18} />}
          >
            Explore Team Mentoring
          </Button>
        </Link>
      </Stack>
    </Stack>
  )
}